import type { Account, Contact, GroupRecord, MessageEvent } from "@veto/domain";
import {
  addMembers,
  assertSelfIsAdmin,
  createEventId,
  createGroupRecord,
  encodeSystemEvent,
  removeMember,
  renameGroup as renameGroupRecord,
  setMemberRole,
  userFacingMessage,
} from "@veto/domain";
import { generateGroupSenderKey, CONTENT_TYPE_GROUP, CONTENT_TYPE_TEXT } from "@veto/protocol";
import { listConversations, saveGroup, upsertConversation } from "@veto/storage";

async function upsertConversationTitle(conversationId: string, title: string) {
  const conv = (await listConversations()).find((c) => c.id === conversationId);
  if (conv) await upsertConversation({ ...conv, title });
}
import { fanoutGroupKey } from "../messaging/groupSend";
import type { Messenger } from "../messaging/messenger";

export function createGroupActions(deps: {
  messenger: Messenger | null;
  account: Account | null;
  contacts: Contact[];
  activeGroup: GroupRecord | null;
  putMessage: (m: MessageEvent) => Promise<void>;
  listMessages: (id: string) => Promise<MessageEvent[]>;
  setActiveGroup: (g: GroupRecord | null) => void;
  setShowCreateGroup: (v: boolean) => void;
  setMessages: (m: MessageEvent[] | ((prev: MessageEvent[]) => MessageEvent[])) => void;
  setToast: (t: string | null) => void;
  refresh: () => Promise<void>;
  openConversation: (id: string) => Promise<void>;
}) {
  async function createGroup(title: string, memberIds: string[]) {
    if (!deps.messenger || !deps.account) return;
    const members = deps.contacts
      .filter((c) => memberIds.includes(c.id))
      .map((c) => ({
        contactId: c.id,
        identityKeyHex: c.messagingPublicKeyHex,
        hybridPublicKeyHex: c.hybridPublicKeyHex,
        displayName: c.displayName,
        role: "member" as const,
      }));
    const group = createGroupRecord({
      title,
      creator: {
        contactId: "self",
        identityKeyHex: deps.account.messagingPublicKeyHex,
        hybridPublicKeyHex: deps.account.hybridPublicKeyHex,
        displayName: deps.account.displayName,
        role: "admin",
      },
      members,
      senderKeyB64: generateGroupSenderKey(),
    });
    await saveGroup(group);
    await upsertConversation({
      id: group.conversationId,
      type: "group",
      title: group.title,
      lastMessagePreview: "Group created",
      lastMessageAt: Date.now(),
      unread: 0,
      pinned: false,
      muted: false,
    });
    try {
      await fanoutGroupKey({ group, contacts: deps.contacts, messenger: deps.messenger! });
    } catch (err) {
      deps.setToast(`Group created locally, but key fan-out failed: ${userFacingMessage(err)}`);
      deps.setShowCreateGroup(false);
      await deps.refresh();
      await deps.openConversation(group.conversationId);
      return;
    }
    await deps.putMessage({
      eventId: createEventId(),
      conversationId: group.conversationId,
      senderDevice: deps.messenger!.selfDevice,
      direction: "out",
      body: encodeSystemEvent("group_created", `Group “${group.title}” created`),
      logicalCounter: Date.now(),
      createdAt: Date.now(),
      status: "backed_up",
      contentType: CONTENT_TYPE_TEXT,
    });
    deps.setShowCreateGroup(false);
    await deps.refresh();
    await deps.openConversation(group.conversationId);
    deps.setToast(`Group “${group.title}” created`);
  }

  async function removeGroupMember(identityKeyHex: string) {
    if (!deps.activeGroup || !deps.account || !deps.messenger) return;
    try {
      assertSelfIsAdmin(deps.activeGroup, deps.account.messagingPublicKeyHex);
    } catch (err) {
      deps.setToast(userFacingMessage(err));
      return;
    }
    const next = removeMember(deps.activeGroup, identityKeyHex, generateGroupSenderKey());
    await saveGroup(next);
    deps.setActiveGroup(next);
    try {
      await fanoutGroupKey({ group: next, contacts: deps.contacts, messenger: deps.messenger! });
    } catch (err) {
      // Local epoch already rotated; the outbox keeps retrying the key copies.
      deps.setToast(`Member removed — key update still sending: ${userFacingMessage(err)}`);
    }
    const removed = deps.activeGroup.members.find((m) => m.identityKeyHex === identityKeyHex);
    await deps.putMessage({
      eventId: createEventId(),
      conversationId: next.conversationId,
      senderDevice: deps.messenger!.selfDevice,
      direction: "out",
      body: encodeSystemEvent(
        "member_removed",
        `${removed?.displayName ?? "Member"} removed`,
      ),
      logicalCounter: Date.now(),
      createdAt: Date.now(),
      status: "backed_up",
      contentType: CONTENT_TYPE_TEXT,
    });
    deps.setMessages(await deps.listMessages(next.conversationId));
    deps.setToast("Member removed");
  }

  async function addGroupMembers(contactIds: string[]) {
    if (!deps.activeGroup || !deps.account || !deps.messenger || contactIds.length === 0) return;
    try {
      assertSelfIsAdmin(deps.activeGroup, deps.account.messagingPublicKeyHex);
    } catch (err) {
      deps.setToast(userFacingMessage(err));
      return;
    }
    const existing = new Set(deps.activeGroup.members.map((m) => m.identityKeyHex.toLowerCase()));
    const newMembers = deps.contacts
      .filter((c) => contactIds.includes(c.id))
      .filter((c) => !existing.has(c.messagingPublicKeyHex.toLowerCase()))
      .map((c) => ({
        contactId: c.id,
        identityKeyHex: c.messagingPublicKeyHex,
        hybridPublicKeyHex: c.hybridPublicKeyHex,
        displayName: c.displayName,
        role: "member" as const,
      }));
    if (newMembers.length === 0) {
      deps.setToast("No new members to add");
      return;
    }
    let next;
    try {
      next = addMembers(deps.activeGroup, newMembers, generateGroupSenderKey());
    } catch (err) {
      deps.setToast(userFacingMessage(err));
      return;
    }
    await saveGroup(next);
    deps.setActiveGroup(next);
    // FR-GRP-003: fan-out new epoch key only — no automatic history package.
    try {
      await fanoutGroupKey({ group: next, contacts: deps.contacts, messenger: deps.messenger! });
    } catch (err) {
      deps.setToast(`Members added locally, but key fan-out failed: ${userFacingMessage(err)}`);
      deps.setMessages(await deps.listMessages(next.conversationId));
      return;
    }
    const names = newMembers.map((m) => m.displayName).join(", ");
    await deps.putMessage({
      eventId: createEventId(),
      conversationId: next.conversationId,
      senderDevice: deps.messenger!.selfDevice,
      direction: "out",
      body: encodeSystemEvent(
        "member_added",
        `${names} added · earlier messages aren't shared with them`,
      ),
      logicalCounter: Date.now(),
      createdAt: Date.now(),
      status: "backed_up",
      contentType: CONTENT_TYPE_TEXT,
    });
    deps.setMessages(await deps.listMessages(next.conversationId));
    deps.setToast(`${names} added`);
  }

  async function changeGroupRole(identityKeyHex: string, role: "admin" | "member") {
    if (!deps.activeGroup || !deps.account || !deps.messenger) return;
    try {
      assertSelfIsAdmin(deps.activeGroup, deps.account.messagingPublicKeyHex);
    } catch (err) {
      deps.setToast(userFacingMessage(err));
      return;
    }
    let next;
    try {
      next = setMemberRole(deps.activeGroup, identityKeyHex, role, generateGroupSenderKey());
    } catch (err) {
      deps.setToast(userFacingMessage(err));
      return;
    }
    await saveGroup(next);
    deps.setActiveGroup(next);
    try {
      await fanoutGroupKey({ group: next, contacts: deps.contacts, messenger: deps.messenger! });
    } catch (err) {
      deps.setToast(`Role changed — key update still sending: ${userFacingMessage(err)}`);
    }
    const member = next.members.find((m) => m.identityKeyHex === identityKeyHex);
    await deps.putMessage({
      eventId: createEventId(),
      conversationId: next.conversationId,
      senderDevice: deps.messenger!.selfDevice,
      direction: "out",
      body: encodeSystemEvent(
        "role_changed",
        `${member?.displayName ?? "Member"} is now ${role === "admin" ? "an admin" : "a member"}`,
      ),
      logicalCounter: Date.now(),
      createdAt: Date.now(),
      status: "backed_up",
      contentType: CONTENT_TYPE_TEXT,
    });
    deps.setMessages(await deps.listMessages(next.conversationId));
    deps.setToast(
      role === "admin"
        ? "Now an admin"
        : "No longer an admin",
    );
  }

  async function renameGroup(title: string) {
    if (!deps.activeGroup || !deps.account || !deps.messenger) return;
    let next;
    try {
      assertSelfIsAdmin(deps.activeGroup, deps.account.messagingPublicKeyHex);
      next = renameGroupRecord(deps.activeGroup, title, generateGroupSenderKey());
    } catch (err) {
      deps.setToast(userFacingMessage(err));
      return;
    }
    if (next.title === deps.activeGroup.title) return;
    await saveGroup(next);
    deps.setActiveGroup(next);
    await upsertConversationTitle(next.conversationId, next.title);
    try {
      await fanoutGroupKey({ group: next, contacts: deps.contacts, messenger: deps.messenger });
    } catch (err) {
      deps.setToast(`Renamed — the update is still sending: ${userFacingMessage(err)}`);
      await deps.refresh();
      return;
    }
    await deps.refresh();
    deps.setToast("Group renamed");
  }

  return { createGroup, removeGroupMember, addGroupMembers, changeGroupRole, renameGroup };
}
